import { useAutoSaveForm } from '../../../components/forms/useAutoSaveForm'
import FormHeader from '../../../components/forms/FormHeader'
import { api } from '../../../lib/api'
import Input from '../../../components/ui/Input'
import Button from '../../../components/ui/Button'

interface Option {
  key: string
  name: string
  color: string
  percentage: number
}

interface State {
  title: string
  question: string
  show_voting: boolean
  options: Option[]
}

interface Props {
  overlayId: string
  initial: Record<string, unknown>
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']

function rebalance(options: Option[]): Option[] {
  const equal = 100 / options.length
  return options.map((o) => ({ ...o, percentage: equal }))
}

function initialState(initial: Record<string, unknown>): State {
  const rawOpts = (initial.options as Option[]) || []
  const options = rawOpts.length >= 2
    ? rawOpts
    : [
        { key: 'a', name: 'Seçenek A', color: '#3b82f6', percentage: 50 },
        { key: 'b', name: 'Seçenek B', color: '#10b981', percentage: 50 },
      ]
  return {
    title: (initial.title as string) || 'OYLAMA',
    question: (initial.question as string) || '',
    show_voting: initial.show_voting !== false,
    options,
  }
}

export default function PollForm({ overlayId, initial }: Props) {
  const form = useAutoSaveForm<State>({
    initial: initialState(initial),
    onSave: async (s) => {
      await api(`/api/overlays/${overlayId}/state`, {
        method: 'PATCH',
        body: JSON.stringify(s),
      })
    },
  })

  function addOption() {
    if (form.draft.options.length >= 5) return
    const nextKey = String.fromCharCode(97 + form.draft.options.length)
    const color = DEFAULT_COLORS[form.draft.options.length] || '#888'
    const opts = [
      ...form.draft.options,
      { key: nextKey, name: `Seçenek ${nextKey.toUpperCase()}`, color, percentage: 0 },
    ]
    form.patch({ options: rebalance(opts) })
  }

  function removeOption(idx: number) {
    if (form.draft.options.length <= 2) return
    form.patch({ options: rebalance(form.draft.options.filter((_, i) => i !== idx)) })
  }

  function updateOption(idx: number, patch: Partial<Option>) {
    form.patch({
      options: form.draft.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    })
  }

  return (
    <div className="space-y-5">
      <FormHeader
        title="Anket"
        subtitle="2-5 seçenekli oylama"
        status={form.status}
        savedAt={form.savedAt}
        error={form.error}
        onUndo={form.canUndo ? form.undo : undefined}
      />

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">Başlık</label>
          <Input value={form.draft.title} onChange={(e) => form.patch({ title: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">Soru</label>
          <Input value={form.draft.question} onChange={(e) => form.patch({ question: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.draft.show_voting}
            onChange={(e) => form.patch({ show_voting: e.target.checked })}
            className="accent-[var(--color-accent)]"
          />
          Oylamayı göster
        </label>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Seçenekler ({form.draft.options.length}/5)</h4>
        <ul className="space-y-2">
          {form.draft.options.map((o, i) => (
            <li key={o.key} className="flex gap-2 items-center">
              <span className="text-xs font-mono w-6 text-[var(--color-text-muted)]">{o.key}</span>
              <Input
                value={o.name}
                onChange={(e) => updateOption(i, { name: e.target.value })}
                className="flex-1"
              />
              <input
                type="color"
                value={o.color}
                onChange={(e) => updateOption(i, { color: e.target.value })}
                className="h-8 w-12 rounded border border-[var(--color-border)] bg-[var(--color-elevated)]"
              />
              <Input
                type="number"
                value={Math.round(o.percentage)}
                onChange={(e) => updateOption(i, { percentage: Number(e.target.value) })}
                className="w-20"
              />
              {form.draft.options.length > 2 && (
                <Button variant="ghost" size="sm" onClick={() => removeOption(i)}>
                  ×
                </Button>
              )}
            </li>
          ))}
        </ul>
        {form.draft.options.length < 5 && (
          <Button variant="ghost" size="sm" onClick={addOption} className="mt-2">
            + Seçenek Ekle
          </Button>
        )}
      </div>
    </div>
  )
}
